import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

const COGNITO_DOMAIN = 'https://percoro-auth.auth.us-east-1.amazoncognito.com';
const CLIENT_ID     = '2no7vkuclaq50ff4tgtoapt4l7';
const API_URL       = process.env.REACT_APP_API_URL ||
  'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev';

// Module-scoped guard. Cognito authorization codes are single-use; replaying
// one returns invalid_grant. This tracks the code value we have already begun
// exchanging so a StrictMode double-invoke or a same-heap remount cannot fire
// a second exchange for the same code. A genuinely new sign-in carries a new
// code and is unaffected.
let exchangingCode: string | null = null;

const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const [error,  setError]  = useState('');
  const [status, setStatus] = useState('Signing you in…');

  useEffect(() => {
    const code      = searchParams.get('code');
    const errParam  = searchParams.get('error');
    const errDesc   = searchParams.get('error_description');

    // Strip the OAuth params from the URL immediately. On mobile Safari and the
    // installed PWA, a back/forward-cache restore or fresh reload would re-read
    // a single-use ?code= and replay the exchange (invalid_grant). Removing it
    // up front makes a reload land on a clean /auth/callback with no code.
    if (code || errParam) {
      window.history.replaceState({}, document.title, '/auth/callback');
    }

    // User cancelled or Google/Cognito returned an error
    if (errParam) {
      if (errParam === 'access_denied') {
        navigate('/login', { replace: true });
        return;
      }
      setError(errDesc || errParam);
      return;
    }

    if (!code) {
      // No code in the URL. If a session already exists, this is a re-entry
      // after a successful exchange — continue to home instead of erroring.
      if (localStorage.getItem('idToken')) {
        navigate('/home', { replace: true });
      } else {
        setError('No authorization code received. Please try signing in again.');
      }
      return;
    }

    // Do not exchange the same code twice (StrictMode, remount within one heap).
    if (exchangingCode === code) {
      return;
    }
    exchangingCode = code;

    handleCallback(code);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async (code: string) => {
    const redirectUri = `${window.location.origin}/auth/callback`;

    try {
      // ── Step 1: Exchange authorization code for tokens ──────────────────────
      setStatus('Completing sign-in…');
      const tokenRes = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type:   'authorization_code',
          client_id:    CLIENT_ID,
          code,
          redirect_uri: redirectUri,
        }).toString(),
      });

      if (!tokenRes.ok) {
        const body = await tokenRes.json().catch(() => ({}));
        // If the Cognito app client has a secret, this exchange must go
        // server-side (Lambda). See #354 follow-up: add
        // my-travel-agent-oauth-callback Lambda if you see a 401 here.
        throw new Error(
          body.error_description ||
          body.error ||
          `Token exchange failed (HTTP ${tokenRes.status})`
        );
      }

      const { id_token, access_token, refresh_token } = await tokenRes.json();

      // ── Step 2: Persist tokens (always use idToken for API calls) ──────────
      setStatus('Setting up your account…');
      localStorage.setItem('idToken',       id_token);
      localStorage.setItem('accessToken',   access_token);
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);

      // ── Step 3: Load profile (creates row on first federated login if Lambda
      //            does upsert; otherwise AppLayout handles it on /home) ────────
      try {
        const profileRes = await fetch(`${API_URL}/users/profile`, {
          headers: { Authorization: `Bearer ${id_token}` },
        });
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          if (profileData.profile) {
            localStorage.setItem('user', JSON.stringify(profileData.profile));
          }
        }
      } catch {
        // Non-fatal — profile will load on /home
      }

      // ── Step 4: Navigate to authenticated home ─────────────────────────────
      navigate('/home', { replace: true });

    } catch (err: any) {
      console.error('[AuthCallback] error:', err);
      // If a token already landed — e.g. a near-simultaneous duplicate exchange
      // where the first call won the race — do not surface an error; continue.
      if (localStorage.getItem('idToken')) {
        navigate('/home', { replace: true });
        return;
      }
      setError(err.message || 'Sign-in failed. Please try again.');
    }
  };

  // ── Error state ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 400, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            If this keeps happening, try signing in with email and password.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{ borderRadius: '30px', textTransform: 'none', px: 4 }}
          >
            Back to Login
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Loading state ───────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress size={48} sx={{ color: '#2D6E6A' }} />
      <Typography color="text.secondary">{status}</Typography>
    </Box>
  );
};

export default AuthCallbackPage;